import classNames from 'classnames';
import { useState, PropsWithChildren, ReactElement } from 'react';

import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import { useAccessibleTextColor } from '~globals/common';

import * as styles from './AnswerCenterStyles.module.css';
import MobileTab from './child-blocks/MobileTab';
import TabButton from './child-blocks/TabButton';
import TabContent from './child-blocks/TabContent';

interface IProps {
    title?: string;
    backgroundsurface?: SupportedBackgroundColor;
    modulewidth?: 'lg' | 'xl';
}

interface ITabProps {
    title: string;
    ctalink: string;
    question: string;
}

const AnswerCenterExplorer = ({
    title = 'Your medicare answer center',
    backgroundsurface = 'bg-surface-100',
    modulewidth = 'lg',
    children = [],
}: PropsWithChildren<IProps>) => {
    const [activeTab, setActiveTab] = useState(0);

    const childItems = (children as ReactElement<ITabProps>[])
        .filter((child) => child && typeof child === 'object' && 'props' in child)
        .slice(0, 6);

    const handleTabClick = (tabNumber: number) => {
        setActiveTab(tabNumber);
    };

    const containerStyles = styles.answerCenter as string;

    const baseTextColor = useAccessibleTextColor(backgroundsurface, 'text-primary-dark');

    const innerwidth = modulewidth === 'xl' ? 'lg' : 'md';

    const trackingName = usePageContext().location.pathname.includes('/new-to-medicare/')
        ? 'NTM Answer Center'
        : 'Answer Center';

    return (
        <section className={classNames(backgroundsurface, containerStyles, 'px-4')}>
            <div
                className={classNames(
                    'py-10 px-4 lg:px-0 lg:py-16',
                    'flex flex-col mx-auto min-h-660',
                    'gap-6 lg:gap-9',
                    'lg:w-full',
                    `max-w-screen-${modulewidth}`
                )}
            >
                <h1
                    className={classNames(
                        'leading-10 text-32 md:text-48 lg:text-40',
                        'font-bold font-poppins text-center',
                        baseTextColor
                    )}
                    dangerouslySetInnerHTML={{ __html: title }}
                />
                {/* DESKTOP*/}
                <div className="hidden lg:block w-full">
                    <ul className="flex border-b border-gray-outline">
                        {childItems.map((tab, index) => {
                            return (
                                <li
                                    key={tab.props.title}
                                    className={classNames(
                                        'flex-1',
                                        'flex items-center justify-center'
                                    )}
                                >
                                    <TabButton
                                        handleTabClick={handleTabClick}
                                        index={index}
                                        isActive={activeTab === index}
                                        title={tab.props.title}
                                        trackingName={trackingName}
                                        isPrimaryBgColorDark={
                                            baseTextColor === 'text-surface-light'
                                        }
                                    />
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <div className="hidden lg:flex justify-center">
                    <div className={`w-full max-w-screen-${innerwidth}`}>
                        {childItems.map((child, i) => {
                            return (
                                <TabContent
                                    {...child.props}
                                    isActive={activeTab === i}
                                    key={child.props.title}
                                    bgColor={backgroundsurface}
                                    trackingName={trackingName}
                                />
                            );
                        })}
                    </div>
                </div>
                {/* TABLET AND MOBILE */}
                <ul className="lg:hidden space-y-6">
                    {childItems.map((child, index) => {
                        return (
                            <li key={child.props.title}>
                                <MobileTab
                                    {...child.props}
                                    handleTabClick={() => handleTabClick(index)}
                                    isActive={activeTab === index}
                                />
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
};

export default AnswerCenterExplorer;
